import type {
    AppliedHeuristicLogMessage,
    AsyncStructureIterator,
    ComponentStageData,
    ConversionCompRef,
    ConversionDal,
    CreateComponentStageArgs,
    ConversionAlgoConfig,
    InnerElementStageHandler,
    MobileAlgoContext,
    Stage,
    StageHandler,
    StageRuntimeStore,
    InnerElementStageData
} from '../types'
import {pointerUtils, type SnapshotDal} from '@wix/document-manager-core'
import type {Pointer} from '@wix/document-services-types'
import {NaryTreeTraversalOrder, ChildrenProperties} from '../enums'
import {ReportableError} from '@wix/document-manager-utils'
import {createStructureIterators, defaultChildrenProperties} from '../conversionDal/utils/structureIterators'
import {ConversionDalNamespaces, ConversionDalStructureNamespaces} from '../conversionDal/constants'
import {getInnerElementPointer} from '../conversionDal/utils/pointer'

export const createComponentStage = (
    {
        traversalOrder = NaryTreeTraversalOrder.PreOrder,
        structuralType = ConversionDalNamespaces.structure,
        childrenProperties = defaultChildrenProperties
    }: CreateComponentStageArgs = {
        traversalOrder: NaryTreeTraversalOrder.PreOrder,
        structuralType: ConversionDalNamespaces.structure,
        childrenProperties: defaultChildrenProperties
    }
): Stage<ComponentStageData> => {
    if (!(traversalOrder in NaryTreeTraversalOrder)) {
        throw new ReportableError({
            errorType: 'InvalidConversionStructureTreeTraversalOrder',
            message: `traversalOrder should be one of the following: ${Object.values(NaryTreeTraversalOrder)}`
        })
    }

    if (!ConversionDalStructureNamespaces.includes(structuralType)) {
        throw new ReportableError({
            errorType: 'InvalidConversionStructuralType',
            message: `structuralType should be one of the following: ${ConversionDalStructureNamespaces}`
        })
    }

    const handlers: StageHandler<ComponentStageData>[] = []
    const innerElementsHandlers: InnerElementStageHandler[] = []

    const _runInnerElements = async (
        componentStageData: ComponentStageData,
        conversionDal: ConversionDal,
        logger?: AppliedHeuristicLogMessage[]
    ): Promise<void> => {
        if (innerElementsHandlers.length === 0) {
            return
        }

        const innerElementRootPointer = getInnerElementPointer({id: componentStageData.componentPointer.id})

        if (!conversionDal.get(innerElementRootPointer)) {
            return
        }

        const innerElementIterators = createStructureIterators(conversionDal, innerElementRootPointer)

        await innerElementIterators.forEachComponentAsync(
            async (pointer: Pointer) => {
                if (pointer.id === componentStageData.componentPointer.id) {
                    return
                }

                const innerElementStageData: InnerElementStageData = {
                    ...componentStageData,
                    innerElementPointer: pointer as ConversionCompRef<ConversionDalNamespaces.innerElements>
                }

                const handlersResults = await Promise.all(
                    innerElementsHandlers.map(handler => handler(innerElementStageData))
                )

                if (logger) {
                    for (const results of handlersResults) {
                        if (results) {
                            logger.push({
                                componentId: componentStageData.componentPointer.id,
                                innerElement: pointer.id,
                                appliedHeuristics: results.appliedHeuristics.filter(Boolean),
                                timestamp: new Date().toISOString()
                            })
                        }
                    }
                }
            },
            [ChildrenProperties.innerElements]
        )
    }

    const _run = async (
        ctx: MobileAlgoContext,
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        iterator: AsyncStructureIterator,
        pageId: string,
        config: ConversionAlgoConfig,
        stageRuntimeStore: StageRuntimeStore,
        logger?: AppliedHeuristicLogMessage[]
    ) => {
        await iterator(async (componentPointer: Pointer) => {
            const componentStageData: ComponentStageData = {
                globalContext: ctx,
                conversionDal,
                initialSnapshot,
                componentPointer,
                pageId,
                config,
                stageRuntimeStore
            }
            const handlersResults = await Promise.all(handlers.map(handler => handler(componentStageData)))

            if (logger) {
                for (const results of handlersResults) {
                    if (results) {
                        logger.push({
                            componentId: componentPointer.id,
                            appliedHeuristics: results.appliedHeuristics.filter(Boolean),
                            timestamp: new Date().toISOString()
                        })
                    }
                }
            }

            await _runInnerElements(componentStageData, conversionDal, logger)
        }, childrenProperties)
    }

    const run = async (
        ctx: MobileAlgoContext,
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        pageId: string,
        config: ConversionAlgoConfig,
        stageRuntimeStore: StageRuntimeStore,
        logger?: AppliedHeuristicLogMessage[]
    ) => {
        if (handlers.length === 0 && innerElementsHandlers.length === 0) {
            return
        }

        const rootPointerInCurrentStructure = pointerUtils.getPointer(config.rootPointer.id, structuralType)

        if (!conversionDal.get(rootPointerInCurrentStructure)) {
            return
        }

        const structureIterators = createStructureIterators(conversionDal, rootPointerInCurrentStructure)

        if (traversalOrder === NaryTreeTraversalOrder.PostOrder) {
            await _run(
                ctx,
                conversionDal,
                initialSnapshot,
                structureIterators.forEachComponentPostOrderAsync,
                pageId,
                config,
                stageRuntimeStore,
                logger
            )
        } else if (traversalOrder === NaryTreeTraversalOrder.PreOrder) {
            await _run(
                ctx,
                conversionDal,
                initialSnapshot,
                structureIterators.forEachComponentAsync,
                pageId,
                config,
                stageRuntimeStore,
                logger
            )
        }
    }

    return {
        register: (handler: StageHandler<ComponentStageData>) => {
            handlers.push(handler)
        },
        registerInnerElementHandler: (handler: any) => {
            innerElementsHandlers.push(handler)
        },
        run
    }
}
